import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, KeyRound, RefreshCw, Clock, ListChecks, History, Save, AlertTriangle,
} from "lucide-react";
import { usePinSecurity } from "@/contexts/PinContext";
import { OPERATION_LABELS, EVENT_LABELS, operationLabel, eventLabel } from "./operationLabels";
import OneDrivePanel from "./OneDrivePanel";
import { ReconciliationPanel } from "@/components/autoescuela/ReconciliationBanner";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_) { return iso; }
}

function PinCard() {
  const pin = usePinSecurity();
  const status = pin.status;
  return (
    <div className="card-surface p-4 space-y-3" data-testid="security-pin-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-orange-500" />
        <h3 className="font-bold">PIN maestro</h3>
      </div>
      <p className="text-xs text-neutral-500">
        Este PIN autoriza todas las operaciones sensibles marcadas más abajo. Tras introducirlo una vez tendrás una sesión segura de {status?.sudo_minutes || 10} minutos.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase text-neutral-500">Estado</div>
          <div className="mt-1 font-bold text-neutral-900" data-testid="security-pin-status">
            {status?.configured ? "Configurado" : "No configurado"}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase text-neutral-500">Sesión segura</div>
          <div className="mt-1 font-bold text-neutral-900" data-testid="security-sudo-status">
            {pin.hasSudo() ? "Activa" : "Requiere PIN"}
          </div>
        </div>
        <div className="rounded-lg border border-neutral-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase text-neutral-500">Bloqueo</div>
          <div className="mt-1 font-bold text-neutral-900" data-testid="security-lock-status">
            {status?.locked ? `${Math.ceil((status?.locked_seconds_remaining || 0)/60)} min` : "Ninguno"}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <button className="btn-primary" onClick={pin.openChange} data-testid="security-change-pin">
          <KeyRound className="h-4 w-4" /> Cambiar PIN
        </button>
        <button className="btn-secondary" onClick={pin.openRecovery} data-testid="security-recover-pin">
          <RefreshCw className="h-4 w-4" /> Recuperar PIN
        </button>
      </div>
    </div>
  );
}

function ConfigCard() {
  const pin = usePinSecurity();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ max_attempts: 5, lockout_minutes: 15, sudo_minutes: 10, protected_operations: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/config");
      setConfig(data);
      setForm({
        max_attempts: data.max_attempts,
        lockout_minutes: data.lockout_minutes,
        sudo_minutes: data.sudo_minutes,
        protected_operations: [...data.protected_operations],
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleOp = (op) => {
    setForm((f) => ({
      ...f,
      protected_operations: f.protected_operations.includes(op)
        ? f.protected_operations.filter((x) => x !== op)
        : [...f.protected_operations, op],
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/security/config", {
        max_attempts: Number(form.max_attempts),
        lockout_minutes: Number(form.lockout_minutes),
        sudo_minutes: Number(form.sudo_minutes),
        protected_operations: form.protected_operations,
      });
      toast.success("Configuración de seguridad actualizada");
      await load();
      await pin.refreshStatus();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (loading || !config) return <div className="card-surface p-4 text-sm text-neutral-500">Cargando…</div>;

  return (
    <div className="card-surface p-4 space-y-4" data-testid="security-config-card">
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-orange-500" />
        <h3 className="font-bold">Comportamiento y bloqueos</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          ["max_attempts", "Intentos permitidos", 1, 20],
          ["lockout_minutes", "Bloqueo tras exceder (min)", 1, 240],
          ["sudo_minutes", "Duración sesión segura (min)", 1, 60],
        ].map(([k, label, min, max]) => (
          <div key={k}>
            <label className="text-[11px] font-semibold mb-1 block text-neutral-600">{label}</label>
            <input
              type="number" min={min} max={max}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`security-config-${k}`}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-10 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
        ))}
      </div>

      <div className="pt-2">
        <div className="flex items-center gap-2 mb-2">
          <ListChecks className="h-4 w-4 text-orange-500" />
          <h4 className="text-sm font-bold">Operaciones protegidas</h4>
        </div>
        <p className="text-xs text-neutral-500 mb-2">
          Marca las acciones que exigirán PIN. Los cambios se aplican inmediatamente para toda la aplicación.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {config.available_operations.map((op) => {
            const on = form.protected_operations.includes(op);
            return (
              <label
                key={op}
                className={"flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm cursor-pointer transition-colors " +
                  (on ? "border-orange-300 bg-orange-50/70" : "border-neutral-200 bg-white hover:bg-neutral-50")}
                data-testid={`security-op-${op}`}
              >
                <span className={on ? "font-semibold text-neutral-900" : "text-neutral-700"}>
                  {OPERATION_LABELS[op] || op}
                </span>
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-orange-500"
                  checked={on}
                  onChange={() => toggleOp(op)}
                />
              </label>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t border-neutral-100">
        <button onClick={save} disabled={saving} className="btn-primary" data-testid="security-config-save">
          <Save className="h-4 w-4" /> {saving ? "Guardando…" : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function AuditCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/security/audit?limit=${limit}`);
      setEntries(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card-surface p-4 space-y-3" data-testid="security-audit-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-orange-500" />
          <h3 className="font-bold">Registro de auditoría</h3>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-md border border-neutral-300 bg-white text-sm h-8 px-2"
            data-testid="security-audit-limit"
          >
            <option value={20}>Últimos 20</option>
            <option value={50}>Últimos 50</option>
            <option value={100}>Últimos 100</option>
            <option value={500}>Últimos 500</option>
          </select>
          <button onClick={load} className="btn-secondary !h-8 !px-2 text-xs" data-testid="security-audit-refresh">
            <RefreshCw className="h-3.5 w-3.5" /> Actualizar
          </button>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-neutral-500">Cargando…</div>
      ) : entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center text-xs text-neutral-500">
          Aún no hay eventos registrados.
        </div>
      ) : (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-neutral-500 border-b border-neutral-200">
              <tr>
                <th className="text-left py-2 px-4">Fecha</th>
                <th className="text-left py-2 px-4">Evento</th>
                <th className="text-left py-2 px-4">Usuario</th>
                <th className="text-left py-2 px-4">Operación</th>
                <th className="text-left py-2 px-4">Estado</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-b border-neutral-100" data-testid={`security-audit-row-${e.id}`}>
                  <td className="py-2 px-4 tabular-nums text-xs text-neutral-600 whitespace-nowrap">{fmtDateTime(e.ts)}</td>
                  <td className="py-2 px-4 font-medium text-neutral-900">{eventLabel(e.event)}</td>
                  <td className="py-2 px-4 text-neutral-700 text-xs">{e.actor_email || "—"}</td>
                  <td className="py-2 px-4 text-neutral-700 text-xs">{e.metadata?.operation ? operationLabel(e.metadata.operation) : "—"}</td>
                  <td className="py-2 px-4">
                    {e.success ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5">OK</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-red-100 text-red-800 text-[10px] font-semibold px-2 py-0.5">Fallo</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function SecurityTab() {
  return (
    <div className="space-y-5 max-w-5xl mx-auto" data-testid="security-tab">
      <PinCard />
      <OneDrivePanel />
      <ReconciliationPanel />
      <ConfigCard />
      <AuditCard />
    </div>
  );
}
